import { yupResolver } from '@hookform/resolvers/yup';
import {
  Grid,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  ICandidate,
  IJob,
  IMappingFormSettingsResponse,
  fetchQuestionsForJob,
  getJobCandidateInteraction,
  amplitude,
} from 'wisestep-api';
import {
  ChatBoxHeader,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  IConversationState,
  PersonalInfo,
  WsButton,
} from 'wisestep-base';

import { CandidateViewData } from '../../../features/candidateLayout/utils/CandidateViewContext';
import CompletedLogo from '../../../images/complete.svg';
import PendingLogo from '../../../images/pending.svg';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { mappingFormSchema } from '../schema/formSchema';
import { submissionFormDefaultValues } from '../utils/fieldDefaultValues';
import { updateMapping, getMappingFailedNotification } from '../utils/saveMapping';

import ActionFooter from './ActionFooter';
import WidgetJobManagement from './WidgetJobManagement';

interface IRecruiterQuestionForm {
  selectedJob?: IJob | null;
  state: IConversationState;
  mappingFormSettings?: IMappingFormSettingsResponse;
  isMapped: boolean;
  candidateViewData?: CandidateViewData | null;
  closeDrawer?: () => void;
  questionAnswer?: IQuestionResponse[] | null;
}
interface IQuestionResponse {
  question: string;
  answer: string;
}

const RecruiterQuestionForm: FC<IRecruiterQuestionForm> = ({
  selectedJob,
  state,
  mappingFormSettings,
  isMapped,
  candidateViewData,
  closeDrawer,
  questionAnswer,
}) => {
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const mapBeforeNavigation = useConversationWidgetStore((state) => state.mapBeforeNavigation);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [jobQuestions, setJobQuestions] = useState<string[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [answers, setAnswers] = useState({});

  const [candidate, setcadidate] = useState<ICandidate>(activityConversation ?? state.candidate);

  useEffect(() => {
    const setInteraction = async (id: string) => {
      const req = await getJobCandidateInteraction(id);
      setcadidate(req.data);
    };
    setInteraction(candidateViewData?.aggregationData?.recentInteractions?.[0].id ?? '');
  }, [candidateViewData]);
  useEffect(() => {
    setcadidate({ ...candidate, questionResponse: questionAnswer });
  }, [questionAnswer]);

  const formOptions = {
    resolver: yupResolver(
      mappingFormSchema(mappingFormSettings, candidate?.profileURLs?.[0] ?? '')
    ),
    defaultValues: submissionFormDefaultValues(candidate, selectedJob),
  };
  const methods = useForm(formOptions);
  const { handleSubmit, watch } = methods;

  useEffect(() => {
    if (candidate?.questionResponse && candidate?.questionResponse?.length > 0) {
      candidate?.questionResponse.map((qa) =>
        setAnswers((prev) => ({ ...prev, [qa.question]: qa.answer }))
      );
    }
  }, [candidate]);

  useEffect(() => {
    if (mapBeforeNavigation) {
      refSubmitButtom?.current?.click();
    }
  }, [handleSubmit, mapBeforeNavigation]);

  const getQuestionsForJob = async () => {
    try {
      if (candidate?.questionResponse && candidate?.questionResponse?.length > 0) {
        const questions = candidate.questionResponse.map((item) => item.question);
        if (questions && questions.length > 0) {
          setJobQuestions(questions);
        }
      } else if (selectedJob?.questions && selectedJob?.questions?.length > 0) {
        setJobQuestions(selectedJob?.questions);
      } else if (selectedJob && selectedJob.id) {
        const response = await fetchQuestionsForJob(selectedJob.id);
        const questions = response.data;
        if (questions && questions.length > 0) {
          setJobQuestions(questions);
        } else {
          console.log('Not able to generate questions, Please Try again!');
        }
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    getQuestionsForJob();
  }, []);

  const handleRadioChange = (question, event) =>
    setAnswers((prev) => ({ ...prev, [question]: event.target.value }));

  const onFormSubmit = async (data: FieldValues) => {
    amplitude('Save_Recruiter_Banner_Questions_Response', {
      clickedOn: 'Save Button',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
    const result = jobQuestions.map((question) => ({
      question: question,
      answer: answers[question] || 'Not Answered',
    }));
    const conversation = { ...candidate, questionResponse: result };
    update(data, conversation);
  };

  const update = async (data: FieldValues, conversation: ICandidate) => {
    setDisableSubmit(false);
    const response = await updateMapping(
      {
        ...data,
        recruiterActivity: { actions: [], results: [] },
        downloadSource: conversation.downloadSource ?? 'NOT_SPECIFIED',
        relocationPreference: watch('relocationList'),
      },
      {
        ...conversation,
        jobId: selectedJob?.id ?? '',
        candidatePersonalId: data.candidatePersonalId ?? null,
      }
    );
    if (response && response?.status != 'fail') {
      setNotification(success('Mapping Updated'));
      closeDrawer && closeDrawer();
    } else {
      setNotification(failed(getMappingFailedNotification(response?.response)));
    }
  };

  const onError = () => setNotification(warning('Please enter required fields'));

  return (
    <>
      {isMapped ? (
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onFormSubmit, onError)}
            style={{ backgroundColor: '#F6F6F9' }}
          >
            <ChatBoxHeader sx={{ backgroundColor: 'white' }}>
              <Grid container>
                <Grid item xs={3}>
                  <Box mt={1}>
                    <WsTypography
                      variant={WsTypoVariants.TITLE}
                      color={WsTypoColors.PRIMARY}
                      bold={true}
                    >
                      Recruiter Questions
                    </WsTypography>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box sx={{ float: 'right' }}>
                    <WidgetJobManagement
                      selectedJob={selectedJob}
                      hideIfNoJob={true}
                      jobFetchFailure={!isEmpty(activityConversation) && isEmpty(selectedJob)}
                      ss={12}
                      hideRemove={true}
                    />
                  </Box>
                </Grid>
              </Grid>
            </ChatBoxHeader>
            <Grid item xs={12} sx={{ maxHeight: '82vh', overflowY: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" marginLeft={'30px'}>
                  Add candidate Responses
                </Typography>
                <img
                  src={
                    candidate?.questionResponse && candidate?.questionResponse?.length > 0
                      ? CompletedLogo
                      : PendingLogo
                  }
                  alt="Artificial Intelligence Logo"
                  style={{
                    width: 100,
                    height: 50,
                    marginRight: 8,
                  }}
                />
              </Box>
              <PersonalInfo
                candidate={candidate}
                purpose="mapping"
                mappingFormSettings={mappingFormSettings}
                showTitle={false}
              />
            </Grid>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '100vh',
                width: '100%',
              }}
            >
              <Grid
                sx={{
                  height: '70%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '90%',
                  padding: '16px',
                  border: '1px solid #ccc',
                  margin: '0 auto',
                  backgroundColor: 'white',
                  zIndex: 1500,
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  {`Interview Questions for ${selectedJob?.title} (${selectedJob?.jobCode})`}
                </Typography>
                <Grid container direction="column" spacing={4} sx={{ marginLeft: '15px' }}>
                  {jobQuestions.map((question, index) => (
                    <Grid item key={index}>
                      <FormControl component="fieldset">
                        <FormLabel
                          component="legend"
                          sx={{
                            color: 'black',
                            '&.Mui-focused': {
                              color: 'black',
                            },
                          }}
                        >
                          {question}
                        </FormLabel>
                        <RadioGroup
                          aria-label={question}
                          name={question}
                          value={answers[question] || ''}
                          onChange={(e) => handleRadioChange(question, e)}
                          defaultValue={'no'}
                        >
                          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="no" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Box>

            <ActionFooter sx={{ backgroundColor: 'white' }}>
              <Grid item container xs={6} justifyContent="flex-start">
                <WsButton type="submit" disabled={disableSubmit} size="large">
                  {activityConversation ? 'Update' : 'Save'}
                </WsButton>
              </Grid>
            </ActionFooter>
          </form>
        </FormProvider>
      ) : (
        <Grid item xs={12} display="flex" justifyContent="center" pt={12}>
          <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.SECONDARY}>
            Please map the candidate.
          </WsTypography>
        </Grid>
      )}
    </>
  );
};

export default RecruiterQuestionForm;
